import useFetch from "@entrilia/shared/hooks/useFetch";
import useSetInterval from "@entrilia/shared/hooks/useSetInterval";
import { logError } from "@entrilia/shared/logging";
import { List, Paper, Stack, Typography } from "@mui/material";
import adminApi from "../../../../api/adminApi";
import type { AssistantTask } from "../../../../api/types/assistantTypes";
import useClientContext from "../../../../context/useClientContext";
import { getTaskPath } from "../assistantPaths";
import type { TaskListKind } from "./TaskListRow";
import TaskListRow from "./TaskListRow";

const REFRESH_MS = 30_000;

interface TaskListProps {
  title: string;
  kind: TaskListKind;
  count: number;
  tasks: AssistantTask[];
  clientCode: string;
}

const TaskList = ({ title, kind, count, tasks, clientCode }: TaskListProps) => {
  if (tasks.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="overline" color="textSecondary">
        {title} · {count}
      </Typography>
      <Paper variant="outlined">
        <List disablePadding>
          {tasks.map((task, index) => (
            <TaskListRow
              key={task.taskId}
              task={task}
              kind={kind}
              to={getTaskPath(clientCode, task.taskId)}
              divider={index < tasks.length - 1}
            />
          ))}
        </List>
      </Paper>
    </Stack>
  );
};

const StartScreenTasks = () => {
  const { clientCode } = useClientContext();
  const [overview, error, { fetchSilent }] = useFetch(adminApi.getAssistantTasksOverview);
  useSetInterval(fetchSilent, REFRESH_MS);

  if (error) {
    logError(error, "[StartScreenTasks] getAssistantTasksOverview");
    return null;
  }

  if (
    !overview ||
    (overview.needsAttention.length === 0 && overview.recentlyFinished.length === 0 && overview.scheduled.length === 0)
  ) {
    return null;
  }

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ width: "100%" }}>
      <TaskList
        title="Needs your attention"
        kind="outcome"
        count={overview.needsAttentionCount}
        tasks={overview.needsAttention}
        clientCode={clientCode}
      />
      <TaskList
        title="Recently finished"
        kind="outcome"
        count={overview.recentlyFinishedCount}
        tasks={overview.recentlyFinished}
        clientCode={clientCode}
      />
      <TaskList
        title="Scheduled"
        kind="scheduled"
        count={overview.scheduled.length}
        tasks={overview.scheduled}
        clientCode={clientCode}
      />
    </Stack>
  );
};

export default StartScreenTasks;
